/**
 * On Air camera focus, overlaid on the On Air preview (bottom-left). Room ignores remote speaker cameras
 * for the layout, Remote ignores the room cam, Both uses both. A focused family that is absent falls back
 * to the other. Live only; persists until changed.
 */

import { Box, IconButton, Tooltip } from '@mui/material';
import { Groups, Videocam, VideoCameraFront } from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { LiveContextFocusMode } from '@/enums/live-context-focus-mode.enum';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import { useUpdateLiveContextMutation } from '@/state/mutations/player/use-update-live-context';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key.tsx';

const FOCUS_OPTIONS: Array<{ mode: LiveContextFocusMode; titleKey: TranslationKey; Icon: SvgIconComponent }> = [
  {
    mode: LiveContextFocusMode.ROOM,
    titleKey: 'flows.detail.perform.focusMode.room' as TranslationKey,
    Icon: Videocam,
  },
  {
    mode: LiveContextFocusMode.BOTH,
    titleKey: 'flows.detail.perform.focusMode.both' as TranslationKey,
    Icon: Groups,
  },
  {
    mode: LiveContextFocusMode.REMOTE,
    titleKey: 'flows.detail.perform.focusMode.remote' as TranslationKey,
    Icon: VideoCameraFront,
  },
];

export function PerformFocusModeOverlay({
  entityRef,
  liveContextState,
}: {
  entityRef: string;
  liveContextState?: LiveContextStateDto;
}) {
  const t = useTranslations();
  const { mutate, isPending } = useUpdateLiveContextMutation();
  const livePicture = useLiveContextStateWithLivePlayState(entityRef, liveContextState);
  const current = livePicture?.focusMode ?? LiveContextFocusMode.BOTH;
  const separator = entityRef.indexOf(':');
  const entityType = entityRef.slice(0, separator) as 'Flow' | 'Event';
  const entityId = entityRef.slice(separator + 1);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 6,
        bottom: 6,
        zIndex: 3,
        display: 'flex',
        gap: 0.5,
        pointerEvents: 'auto',
      }}
    >
      {FOCUS_OPTIONS.map(({ mode, titleKey, Icon }) => {
        const isActive = current === mode;
        return (
          <Tooltip key={mode} title={t(titleKey)}>
            <span>
              <IconButton
                onClick={() => {
                  if (isActive || separator < 0 || !entityId) return;
                  mutate({ entityType, entityId, focusMode: mode });
                }}
                color={isActive ? 'error' : 'inherit'}
                size="medium"
                disabled={isPending}
                aria-pressed={isActive}
                aria-label={t(titleKey)}
                sx={{ color: isActive ? undefined : 'rgba(255,255,255,0.85)', p: 0.75 }}
              >
                <Icon sx={{ fontSize: 24 }} />
              </IconButton>
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );
}
